/**
 * THE card renderer. Every funnel screen renders the customer's card through
 * this component and nothing else, so the card is pixel-identical on design,
 * delivery, confirmation and order.
 *
 * Contract: it takes the canonical DesignedCard and a display size. Zoom and
 * position are applied to the AVATAR inside the card (the card itself never
 * scales — scaling the card was the reason the S5 zoom control appeared dead).
 *
 * No preview-vs-final branch exists: there is no brand/placeholder prop, so the
 * design, details, confirm, confirmed and shared cards are the same render.
 */
import { CardFace } from "@/components/consumer/chrome";
import type { DesignedCard } from "@/lib/designed-card";
import { cn } from "@/lib/utils";

type Props = {
  card: DesignedCard;
  /** Fallback art when the design has no avatar yet (style placeholder). */
  fallbackArt?: string | null;
  alt?: string;
  /** Width utility classes for this screen (the ONLY per-screen difference). */
  className?: string;
  /**
   * Display mode. TRUE (default) = locked read-only preview: no pointer
   * interaction at all, so the avatar can't be dragged or nudged. Only the
   * design step (S5) opts out by passing readOnly={false}.
   */
  readOnly?: boolean;
  children?: React.ReactNode;
} & Omit<React.HTMLAttributes<HTMLDivElement>, "className" | "children">;

export function DesignedCardFace({ card, fallbackArt, alt, className, readOnly = true, children, ...rest }: Props) {
  const { avatarUrl, colour, orientation, avatarZoom, avatarPosition } = card;
  return (
    <CardFace
      color={colour}
      art={avatarUrl ?? fallbackArt ?? null}
      alt={alt}
      className={cn(
        orientation === "landscape" ? "aspect-[412/260]" : "aspect-[260/412]",
        readOnly && "pointer-events-none select-none touch-none",
        className,
      )}
      artStyle={{
        transform: `translate(${avatarPosition.x - 50}%, ${avatarPosition.y - 50}%) scale(${avatarZoom / 100})`,
        transformOrigin: "center",
      }}
      data-card-colour={colour}
      data-card-zoom={Math.round(avatarZoom)}
      data-card-pos={`${Math.round(avatarPosition.x)},${Math.round(avatarPosition.y)}`}
      data-card-locked={readOnly ? "true" : "false"}
      aria-readonly={readOnly ? true : undefined}
      {...rest}
    >
      {children}
    </CardFace>
  );
}


export default DesignedCardFace;
